<template>
  <div>
    <text-editor-table-menu
      :editor="editor"
      :editable="editable"
    />
    <v-card-text class="px-0">
      <text-editor-bubble-menu
        :feature-table="featureTable"
        :feature-math="featureMath"
        :feature-code="featureCode"
      />

      <text-editor-floating-menu
        v-if="floatingMenu"
        :feature-table="featureTable"
        :feature-math="featureMath"
        :feature-code="featureCode"
      />


      <editor-content
        :editor="editor"
        class="px-7"
        :class="{
          editable: editable,
        }"
      />
    </v-card-text>

    <slot
      name="bellow"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, provide } from "vue"
import { Editor, EditorContent } from "@tiptap/vue-2"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import js from "highlight.js/lib/languages/javascript"
import { common, createLowlight } from "lowlight"
import Table from "@tiptap/extension-table"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import TableRow from "@tiptap/extension-table-row"
import { Mathematics } from "@tiptap-pro/extension-mathematics"
import Placeholder from "@tiptap/extension-placeholder"
import CharacterWordLimit from "./extensions/words_limit"
import useTextEditorButtons from "./useTextEditorButtons"
import indentHandler from "@/components/shared/text_editor/extensions/indentHandler"
import TextEditorFloatingMenu from "./floating_menu.vue"
import TextEditorBubbleMenu from "./bubble_menu.vue"
import TextEditorTableMenu from "./table_menu.vue"
import osDetection from "../../../helpers/os_detection"
import { useDebounceFn, computedEager } from "@vueuse/core"

const lowlight = createLowlight(common)
lowlight.register( { js })

export default {
  name: "TextEditor",
  components: {
    EditorContent,
    TextEditorFloatingMenu,
    TextEditorBubbleMenu,
    TextEditorTableMenu,
    /* eslint-disable vue/no-unused-components */
    Table, // eslint-disable-line vue/no-reserved-component-names
    TableCell,
    TableHeader,
    TableRow,
    /* eslint-enable vue/no-unused-components */
  },

  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    editable: {
      type: Boolean,
      default: true,
    },
    limit: {
      type: Number,
      default: null,
    },
    limitType: {
      type: String,
      default: "characters",
    },
    featureTable: {
      type: Boolean,
      default: true,
    },
    featureMath: {
      type: Boolean,
      default: true,
    },
    featureCode: {
      type: Boolean,
      default: true,
    },
    floatingMenu: {
      type: Boolean,
      default: true,
    },
    minHeight: {
      type: String,
      default: "75px",
    },
  },
  emits: ["focus", "input"],

  setup(props, { emit }) {
    const EMIT_INPUT_DEBOUNCE_TIME = 1000
    const EMIT_INPUT_MAX_WAIT = 10000
    const editor = ref(null)
    const createTableDialog = ref(false)
    const floatingAddMenuIsOpen = ref(false)

    provide("editor", editor)

    const controlKey = computed(() => {
      return osDetection() === "MacOS" ? "⌘" : "Ctrl"
    })

    // Provide the controlKey for child components
    provide("controlKey", controlKey.value)

    // Methods
    const currentSelectionContent = () => {
      const { view, state } = editor.value
      const { from, to } = view.state.selection
      return state.doc.textBetween(from, to, "")
    }

    const currentCaretPosition = () => {
      return editor.value.view.state.selection.to
    }

    const wordsCount = computedEager(() => editor.value?.storage?.characterCount?.words())
    const charsCount = computedEager(() => editor.value?.storage?.characterCount?.characters())
    provide("wordsCount", wordsCount)
    provide("charsCount", charsCount)


    const lastChangeIsEmitted = ref(true)
    const emitInput = () => {
      emit("input", editor.value.getHTML())
      lastChangeIsEmitted.value = true
    }
    const emitInputDebouncer = useDebounceFn(emitInput, EMIT_INPUT_DEBOUNCE_TIME, { maxWait: EMIT_INPUT_MAX_WAIT })
    const debouncedEmitInput = () => {
      lastChangeIsEmitted.value = false
      emitInputDebouncer()
    }

    const focus = () => {
      editor.value.view.focus()
    }

    // We initialize useTextEditorButtons but don't use it directly in this component
    // It will be consumed by child components that need access to the buttons
    const { marksButtons, nodesButtons, tablesButtons } = useTextEditorButtons(editor, {
      featureTable: props.featureTable,
      featureMath: props.featureMath,
      featureCode: props.featureCode,
      createTableDialog,
    })

    // Watch for value changes
    watch(() => props.value, (value) => {
      const isSame = editor.value.getHTML() === value
      if (isSame) {
        return
      }
      editor.value.commands.setContent(value, false)
    })

    // Initialize editor on mount
    onMounted(() => {
      const extensions = [
        {
          enabled: true,
          object: Underline,
        },
        {
          enabled: true,
          object: CodeBlockLowlight.configure({
            lowlight,
          }),
        },
        {
          enabled: props.featureTable,
          object: Table.configure({
            resizable: true,
          }),
        },
        {
          enabled: props.featureTable,
          object: TableRow,
        },
        {
          enabled: props.featureTable,
          object: TableHeader,
        },
        {
          enabled: props.featureTable,
          object: TableCell,
        },
        {
          enabled: props.featureMath,
          object: Mathematics,
        },
        {
          enabled: true,
          object: Placeholder.configure({
            placeholder: (editor) =>
              editor?.isFocused ? null : props.placeholder,
          }),
        },
        {
          enabled: true,
          object: CharacterWordLimit.configure({
            limit: props.limit,
            limitType: props.limitType,
          }),
        },
        {
          enabled: true,
          object: StarterKit.configure({
            heading: {
              levels: [1, 2, 3],
            },
            listItem: {
              HTMLAttributes: {
                class: "list-disc",
              },
            },
            codeBlock: false,
          }),
        },
        {
          enabled: true,
          object: indentHandler,
        },
      ]
        .filter((extension) => extension.enabled)
        .map((extension) => extension.object)

      editor.value = new Editor({
        content: props.value,
        editable: props.editable,
        extensions: extensions,
        editorProps: {
          attributes: {
            class: "outline-none",
          },
        },
        onUpdate: () => {
          debouncedEmitInput()
        },
      })

    })

    // Clean up editor on component destroy
    onBeforeUnmount(() => {
      if (!lastChangeIsEmitted.value) emitInput()
      editor.value.destroy()
    })

    return {
      editor,
      createTableDialog,
      floatingAddMenuIsOpen,
      controlKey,
      currentSelectionContent,
      currentCaretPosition,
      wordsCount,
      charsCount,
      emitInput,
      focus,
      marksButtons,
      nodesButtons,
      tablesButtons,
    }
  },
}
</script>

<style lang="scss">
.outline-none {
  outline: 0 solid transparent;
}
.ProseMirror ul li.list-disc {
  list-style-type: disc;
}
pre > code {
  color: inherit;
  padding: 0;
  background-color: transparent !important;
  font-size: 0.8rem;
  line-height: 1.2rem;
  display: block;
}

.ProseMirror pre {
  background: #f0f0f0;
  color: #000;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}

.ProseMirror table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0;
  overflow: hidden;
}

.ProseMirror table td,
.ProseMirror table th {
  min-width: 1em;
  border: 2px solid #ced4da;
  padding: 3px 5px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;

  > * {
    margin-bottom: 0;
  }
}

.ProseMirror th {
  font-weight: bold;
  text-align: left;
  background-color: #f1f3f5;
}

.ProseMirror table .selectedCell:after {
  z-index: 2;
  position: absolute;
  content: "";
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(200, 200, 255, 0.4);
  pointer-events: none;
}

.ProseMirror table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: -2px;
  width: 4px;
  background-color: #adf;
  pointer-events: none;
}

.ProseMirror table p {
  margin: 0;
}

.tableWrapper {
  padding: 1rem 0;
  overflow-x: auto;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}

.Tiptap-mathematics-editor {
  background: #202020;
  color: #fff;
  font-family: monospace;
  padding: 0.2rem 0.5rem;
}

.Tiptap-mathematics-render {
  cursor: pointer;
  padding: 0 0.25rem;
  transition: background 0.2s;

  &:hover {
    background: #eee;
  }
}

.Tiptap-mathematics-editor,
.Tiptap-mathematics-render {
  border-radius: 0.25rem;
  display: inline-block;
}

.ProseMirror h1 {
  font-size: 2rem;
  font-weight: 700;
  margin: 1rem 0;
}
.ProseMirror h2 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1rem 0;
}
.ProseMirror h3 {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 1rem 0;
}

/*
    PM create a `p` inside each `li`. We have to remove the margin.
    https://github.com/ueberdosis/tiptap/issues/118#issuecomment-1210705173
  */
.ProseMirror li > p {
  margin: 0;
}

/* Placeholder (at the top) */
.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

.editable .ProseMirror {
  min-height: v-bind(minHeight);
}

/*
  Fix for  floating toolbar not displayed in production only.
  Borrowed from https://github.com/ueberdosis/tiptap/issues/4851
*/
.tippy-content div {
  visibility: visible !important;
}
</style>
